/**
 * Tiptap focus feature. Utilities for syncing click and selection on
 * <RichtextColumnCell /> with Tiptap editor.
 *
 * Handles capturing click position, calculating selection rectangles, and
 * restoring cursor positions or selections when editor mounts.
 *
 * The feature is coordinated via Grid store and actions.
 */

import { type Editor } from '@tiptap/core'

import type { RectBounds } from '@/utils/events'

import { TASK_ITEM_SELECTOR } from '@/features/richtext/nodes/taskitem/taskitem.utils'
import { gridState } from '@/features/rundown/grid/grid.store'

/**
 * Checks for text selection and calculates bounding rectangle.
 *
 * We capture the position where a user clicked, so we can place the Tiptap
 * cursor in the same position. But if a user double clicks on a word, the word
 * is selected first before editing is enabled. This method captures any
 * selection and passes it on, so we can re-create the highlight in Tiptap too.
 */
export function captureCellSelectionRect(): RectBounds | null {
  const selection = window.getSelection()

  if (selection && !selection.isCollapsed && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0)
    const rect = range.getBoundingClientRect()

    // Clear selection immediately to prevent visual flash or invalid selection.
    selection.removeAllRanges()

    // Fix: Only capture text selections (double-clicked words)
    // Ignore selections on block or pseudo elements.
    const isTextSelection = range.startContainer.nodeType === 3 // `Node.TEXT_NODE`
    if (!isTextSelection) return null

    return {
      top: rect.top,
      left: rect.left,
      bottom: rect.bottom,
      right: rect.right,
    }
  }

  // No selection
  return null
}

/**
 * Focuses editor, restoring users intended click location and text selection.
 *
 * Used in `onCreate` lifecycle method in `useEditor()`.
 */
export function handleEditorFocus(editor: Editor) {
  const { clickCoords } = gridState.editor

  if (!clickCoords) {
    // Fallback: Focus end of doc
    focusEndOfDoc(editor)
    return
  }

  const { x, y, selectionRect } = clickCoords

  // Try to select and focus at location
  if (selectionRect && trySelectAndFocusRect(editor, selectionRect)) {
    return // Exit on success
  }

  // Fallback to just focus at location
  focusAtCoordinates(editor, x, y)
}

/**
 * Coordinates at the end of a task item's text, from its preview checkbox.
 *
 * Used as click coordinates, so Enter on a checkbox puts the cursor at the end
 * of that task item, instead of the end of the doc.
 */
export function captureTaskItemCaretCoords(
  checkbox: HTMLInputElement,
): { x: number; y: number; selectionRect: null } | null {
  const content = checkbox
    .closest(TASK_ITEM_SELECTOR)
    ?.querySelector(':scope > div')
  if (!content) return null

  const range = document.createRange()
  range.selectNodeContents(content)
  const rects = range.getClientRects()

  // Empty task item has no text rects, fall back to its content box
  const last = rects[rects.length - 1] ?? content.getBoundingClientRect()

  return {
    x: last.right - 1,
    y: last.top + last.height / 2,
    selectionRect: null,
  }
}

// #region ============== INTERNAL HELPERS

function focusEndOfDoc(editor: Editor) {
  editor.commands.focus('end', { scrollIntoView: false })
}

function focusAtCoordinates(editor: Editor, x: number, y: number) {
  // Map screen coords to editor text position
  const result = editor.view.posAtCoords({ left: x, top: y })

  if (result && result?.pos !== null) {
    // Focus and set cursor if a valid position found
    editor.commands.focus(result.pos, { scrollIntoView: false })
  } else {
    // Fallback: Focus end of doc if outside boundaries
    focusEndOfDoc(editor)
  }
}

function trySelectAndFocusRect(editor: Editor, rect: RectBounds): boolean {
  // Ensure end points are at least slightly ahead of start points (for single char selection)
  const startX = rect.left + 1
  const startY = rect.top + 1
  const endX = Math.max(rect.left + 2, rect.right - 1)
  const endY = Math.max(rect.top + 2, rect.bottom - 1)

  const startPos = editor.view.posAtCoords({ left: startX, top: startY })
  const endPos = editor.view.posAtCoords({ left: endX, top: endY })

  if (!startPos?.pos || !endPos?.pos) return false // Failed

  const from = Math.min(startPos.pos, endPos.pos)
  const to = Math.max(startPos.pos, endPos.pos)

  // Set selection and focus the editor simultaneously
  editor.chain().setTextSelection({ from: from, to: to }).focus().run()

  return true // Success
}

// #endregion
